import {connect} from 'react-redux';

import {AsyncHandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {AppState} from '../../../../store/appState';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {updateShouldShowNewTagInCellsAction} from '../../../../store/data/preferences/preferencesActions';
import {
  getShouldShowNewTagInCells,
  getShouldShowNewTagInWorkspaces,
} from '../../../../store/data/preferences/preferencesSelectors';
import {getCanCreateCompanyTags} from '../../../../store/data/tags/tagsSelectors';
import {
  getFlatActiveOrPendingTeammates,
  getUserId,
  getUserNamespace,
} from '../../../../store/data/teammates/teammatesSelectors';
import {getUserTeams} from '../../../../store/data/teams/teamsSelectors';
import {
  getHasPreventPinTeamTags,
  getHasTagDescription,
} from '../../../../store/featureFlags/featureFlagsSelectors';
import {CreateTagDialog} from './createTagDialog';
import {TagTemplate} from './tagEditorBody';

/*
 * Props.
 */

interface CreateTagDialogContainerProps {
  onOkClick: (tagTemplate: TagTemplate, hasPreventPinTeamTags: boolean) => Promise<void>;
  initialNamespace?: string;
}

/*
 * Selectors.
 */

function getInitialNamespace(_state: AppState, props: CreateTagDialogContainerProps) {
  return props.initialNamespace;
}

function makeGetInitialNamespace() {
  return createSelector(
    [getUserTeams, getUserNamespace, getInitialNamespace, getHasPermission],
    (teams, userNamespace, initialNamespace, hasPermission) => {
      // If a value was provided (e.g. creating a subtag), use it.
      if (initialNamespace) {
        return initialNamespace;
      }

      const teamsWithPermission = teams.filter((t) => hasPermission(t.namespace, PermissionsEnum.EDIT_TAG));

      // Otherwise, if the user has no teams, the initial namespace must be their own namespace.
      if (teamsWithPermission.length === 0) {
        return userNamespace;
      }

      // Finally, they have multiple teams to choose from, so let them.
      return undefined;
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CreateTagDialogContainerProps>()(() => ({
  userId: getUserId,
  shouldShowNewTagInWorkspaces: getShouldShowNewTagInWorkspaces,
  initialNamespace: makeGetInitialNamespace(),
  shouldShowNewTagInCells: getShouldShowNewTagInCells,
  hasPermission: getHasPermission,
  teammates: getFlatActiveOrPendingTeammates,
  hasPreventPinTeamTags: getHasPreventPinTeamTags,
  hasTagDescription: getHasTagDescription,
  canCreateCompanyTags: getCanCreateCompanyTags,
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  updateShouldShowNewTagInCellsAction,
}));

export const CreateTagDialogContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const onOkClick: AsyncHandlerOf<TagTemplate> = async (template) => {
      dispatchProps.updateShouldShowNewTagInCellsAction(template.showInCells);

      return ownProps.onOkClick(template, stateProps.hasPreventPinTeamTags);
    };

    return {
      ...stateProps,
      ...ownProps,
      onOkClick,
    };
  },
)(CreateTagDialog);
